import { useEffect, useState } from 'react';
import { Alert, Button, Stack, TextField } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useGetLojaAtualQuery, useLazyResolverIdentificadorTabletQuery } from '../api';
import { LeitorQr } from './leitor-qr';

/**
 * Campo onde o cliente informa a comanda — digitando, lendo o QR, ou os dois.
 *
 * O mesmo campo serve os dois momentos em que a comanda é pedida: ao fechar o
 * pedido e ao ver a conta. A loja escolhe o modo em `tabletEntradaComanda`:
 *
 *  - `DIGITAR` — só o campo de número.
 *  - `QRCODE`  — só a leitura: a câmera abre sozinha ao entrar na tela e NÃO
 *    existe campo para digitar. É decisão da loja que quer a comanda amarrada à
 *    etiqueta física; se a câmera falhar, a tela manda chamar o garçom.
 *  - `AMBOS`   — os dois na tela, com o QR em destaque.
 *
 * Sobre a abertura automática: o Safari do iPad pode exigir um toque para
 * liberar a câmera. Quando ele recusa, o diálogo mostra o motivo e o botão
 * grande continua na tela — o primeiro toque vale como gesto e resolve.
 *
 * O QR lido pode ser o número puro da comanda, a etiqueta assinada antiga ou a
 * URL do canal de celular: quem traduz para número é o servidor, que também
 * recusa QR de outra loja. O app NUNCA navega para a URL lida — isso trocaria o
 * tenant e o tablet perderia a loja fixada.
 */
export function CampoComanda({
  valor,
  aoMudar,
  rotulo,
  autoFocus,
  inputRef,
  helperText,
  erro,
  aoConfirmar,
  tipoEsperado = 'COMANDA',
}: {
  valor: string;
  aoMudar: (valor: string) => void;
  rotulo: string;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  helperText?: string;
  erro?: string | null;
  /** Chamado quando o QR resolveu — dá para seguir sozinho, sem novo toque. */
  aoConfirmar?: (numero: string) => void;
  /** O QR lido precisa ser deste tipo. A loja tem QR de mesa colado na mesa, e
   *  ler o da mesa num campo de comanda gravaria o número errado. */
  tipoEsperado?: 'MESA' | 'COMANDA';
}): JSX.Element {
  const { data: loja } = useGetLojaAtualQuery();
  const [resolver, { isFetching }] = useLazyResolverIdentificadorTabletQuery();
  const [leitorAberto, setLeitorAberto] = useState(false);
  const [erroLeitura, setErroLeitura] = useState<string | null>(null);
  const [jaAbriuSozinho, setJaAbriuSozinho] = useState(false);

  const entrada = loja?.configuracao.tabletEntradaComanda ?? 'DIGITAR';
  // A configuração é do TABLET. O mesmo campo aparece no QR de mesa/comanda pelo
  // celular, e ali quem manda é o aparelho do cliente — não a config da loja.
  const ehTablet = loja?.identificador.tipo === 'TABLET';
  const podeLer = ehTablet && (entrada === 'QRCODE' || entrada === 'AMBOS');
  const soLeitura = podeLer && entrada === 'QRCODE';

  // Câmera direto ao abrir a tela — uma vez. Se o cliente fechar, fica o botão;
  // reabrir sozinho a cada fechada prenderia o cliente num laço.
  useEffect(() => {
    if (!soLeitura || jaAbriuSozinho) return;
    setJaAbriuSozinho(true);
    setLeitorAberto(true);
  }, [soLeitura, jaAbriuSozinho]);

  async function aoLer(lido: string): Promise<void> {
    setErroLeitura(null);

    const r = await resolver({ valor: lido, tipo: tipoEsperado })
      .unwrap()
      .catch(() => null);

    if (!r) {
      setErroLeitura('Este QR Code não é de uma comanda desta loja. Procure o código da sua comanda.');
      return;
    }

    // A loja também tem QR de MESA colado no salão: ler o da mesa aqui gravaria
    // o número da mesa como se fosse comanda.
    if (r.tipo !== tipoEsperado) {
      const lidoRotulo = r.tipo === 'MESA' ? 'mesa' : 'comanda';
      const pedido = tipoEsperado === 'MESA' ? 'mesa' : 'comanda';
      setErroLeitura(
        `Este QR Code é da ${lidoRotulo} ${r.numero}, não de uma ${pedido}. Procure o código da ${pedido}.`,
      );
      return;
    }

    setLeitorAberto(false);
    aoMudar(r.numero);
    aoConfirmar?.(r.numero);
  }

  return (
    <Stack spacing={1.5}>
      {podeLer && (
        <Button
          variant={soLeitura ? 'contained' : 'outlined'}
          size="large"
          startIcon={<QrCodeScannerIcon />}
          onClick={() => {
            setErroLeitura(null);
            setLeitorAberto(true);
          }}
          disabled={isFetching}
          sx={{ py: 1.5, fontWeight: 700 }}
        >
          {soLeitura ? 'Abrir câmera para ler a comanda' : 'Ler QR Code da comanda'}
        </Button>
      )}

      {erroLeitura && <Alert severity="warning">{erroLeitura}</Alert>}

      {/* Em QRCODE não existe campo: a comanda vem da etiqueta, por decisão da loja. */}
      {!soLeitura && (
        <TextField
          label={rotulo}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && aoConfirmar) aoConfirmar(valor);
          }}
          fullWidth
          required
          autoFocus={autoFocus}
          inputRef={inputRef}
          inputProps={{ inputMode: 'numeric' }}
          error={!!erro}
          helperText={erro ?? helperText ?? (podeLer ? 'Ou informe o número da comanda.' : undefined)}
        />
      )}

      <LeitorQr
        aberto={leitorAberto}
        titulo="Aponte para o QR Code da comanda"
        podeDigitar={!soLeitura}
        aoLer={(lido) => void aoLer(lido)}
        aoFechar={() => setLeitorAberto(false)}
        aoPedirDigitar={soLeitura ? undefined : () => setLeitorAberto(false)}
        iluminar={ehTablet}
      />
    </Stack>
  );
}
